import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { Store } from '../../stateManagement/Store';
import {
    IAggregationHelper,
    IAuthorizationService,
    IDashboardHelperService,
    IDateRangeService,
    IExportToCsvService,
    IFormatNumberHelper,
    ILogbookService,
    ILoggerService,
    ISeriesSelectorService,
    ISeriesService,
    IStoreService,
    ITableWidgetService,
    IValueFormattingService,
    IsparklineHelperService
} from '../../services';
import {
    MomentHelper,
    Roles,
    WideError,
    aggregationModes,
    groupSelected,
    guidEmpty,
    guidGroupValidationPageLink,
    isEditMode,
    missingSeries,
    momentFormat,
    refreshClick,
    toDictionary
} from '../../common';
import { ValueTypes } from '../../common/ValueTypes';
import {
    ActualDataType,
    AggregationMode,
    AlarmRange,
    ComponentDefinition,
    Dashboard,
    Dictionary,
    DigitalSetSimpleDictionary,
    ErrorLevel,
    Group,
    IBindable,
    IValueViewModel,
    Identifier,
    LogbookEntryDto,
    SparkLineData,
    TagDateValue,
    TagDefinition,
    TagStringValueDto,
    VariableType,
    WidgetValues
} from '../../models';
import { DateViewModel, FakeTagDateValue, IWidget, RowViewModel } from '../../widgets';
import { IRootScope } from 'IRootScope';
import { TableWidget } from './table.widget';
import { StoreElement } from '../../stateManagement';
import { sparklineHeights } from '../../common/sparklineHeight';
import { RowConfiguration } from './tableRows/tableRow.configuration';

class TableController implements angular.IComponentController {
    aggregationColumnMode: string;
    beginDate: string;
    boundData: WidgetValues;
    colStyle: {} = {};
    dashboardNames: Dictionary<string>;
    dashboards: Dashboard[];
    dates: DateViewModel[];
    digitalSets: DigitalSetSimpleDictionary;
    displayedRows: RowViewModel[];
    endDate: string;
    hasManualInputRole = false;
    identifiers: Dictionary<Identifier>;
    isEditMode: boolean;
    isLoading: boolean;
    isStoreLoaded: boolean;
    item: TableWidget;
    loadingMessage: string;
    momentFormat: string;
    nameStyle: {} = {};
    // TODO should be readonly but is modified inside component
    rowViewModels: RowViewModel[];
    stringValues: TagStringValueDto[];
    tags: Dictionary<TagDefinition>;
    trendHeight: number = 16;
    variablesTypes: Dictionary<VariableType>;
    viewModels: Dictionary<IValueViewModel>;

    readonly isBound: boolean;
    readonly isLoadingOutside?: boolean;
    readonly selectedGroup: Group;

    private _colsCount = 1;
    private _garbages: (() => void)[] = [];
    private _rowsConfiguration: IBindable[];
    private _whenLoaded: angular.IDeferred<void>;

    static $inject = [
        '$scope',
        '$q',
        '$rootScope',
        '$translate',
        '$state',
        'storeService',
        'tableWidgetService',
        'dateRangeService',
        'seriesService',
        'sparklineHelperService',
        'aggregationHelper',
        'formatNumberHelper',
        'exportToCsvService',
        'logbookService',
        'authorizationService',
        'loggerService',
        'dashboardHelperService',
        'seriesSelectorService',
        'valueFormattingService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _$state: angular.ui.IStateService,
        private _storeService: IStoreService,
        private _tableWidgetService: ITableWidgetService,
        private _dateRangeService: IDateRangeService,
        private _seriesService: ISeriesService,
        private _sparkLineHelper: IsparklineHelperService,
        private _aggregationHelper: IAggregationHelper,
        private _formatNumberHelper: IFormatNumberHelper,
        private _exportToCsvService: IExportToCsvService,
        private _logbookService: ILogbookService,
        private _authorizationService: IAuthorizationService,
        private _loggerService: ILoggerService,
        private _dashboardHelperService: IDashboardHelperService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _valueFormattingService: IValueFormattingService
    ) {
    }

    $onInit() {
        if (!this.item.configuration.nameConfiguration) {
            this.item.configuration.nameConfiguration = ({} as any);
        }
        this.trendHeight = sparklineHeights[this.item.configuration.rowSpacing];

        this.aggregationColumnMode = this.item.configuration.aggregationColumnMode
            ? aggregationModes.find(mode => mode.key === this.item.configuration.aggregationColumnMode)?.value
            : undefined;

        // Connect to store before loading any data as loading state is required
        this._whenLoaded = this._$q.defer<void>();
        const cleaner = this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.Tags,
                StoreElement.Dashboards,
                StoreElement.DigitalSets,
                StoreElement.Identifiers,
                StoreElement.ValueViewModels,
                StoreElement.Variables
            ]
        );
        this._garbages.push(cleaner);

        if (!this.isBound) {
            this._$scope.$watch(
                () => this.item.configuration.rows,
                newValue => this.updateSeries(newValue),
                true
            );

            this._$scope.$watchCollection(
                () => this.item.configuration.nameConfiguration,
                newValue => {
                    newValue.forceName = !(
                        newValue.hasGroupName ||
                        newValue.hasName ||
                        newValue.hasShortComment ||
                        newValue.hasUnit ||
                        newValue.hasValueType
                    );
                }
            );

            this._$scope.$on(groupSelected, () => {
                this.updateSeries(this.item.configuration.rows);
            });
        } else {
            this._rowsConfiguration = this.item.configuration.rows;

            const queryRows = this.item.configuration.rows.filter(r => r.isQuery);
            if (queryRows.length > 0) {

                this._seriesSelectorService
                    .getQueriesSeriesSelectorViewModels()
                    .then(data => {
                        queryRows.forEach(r => {
                            const qvm = _.find(data, d => d.guid === r.guid);
                            if (!qvm) {
                                this.item.setError(new WideError(ErrorLevel.Error, 'Missing query'));
                            } else if (qvm.valueTypes?.filter(d => !!d /* can be ''*/).length > 0 && qvm.valueTypes.indexOf(r.value) < 0) {
                                this.item.setError(new WideError(ErrorLevel.Error, 'Query with missing value type'));
                            }
                        });
                    });
            }
            this._$scope.$watch(
                () => this.rowViewModels,
                () => this.updateBoundData()
            );
        }

        this._$scope.$watch(() => this.item.configuration.aggregation, (newValue) => {
            if (newValue) {
                if (!this.item.configuration.dateFormat) {
                    this.setMomentFormat();
                } else {
                    this.momentFormat = this.item.configuration.dateFormat;
                }
            }
            this.refresh()();
        }, true);

        this._$scope.$watch(() => this.item.configuration.dateFormat, (newValue) => {
            if (newValue) {
                this.momentFormat = newValue;
            } else {
                this.setMomentFormat();
            }
        });

        this._$scope.$watch(() => this.item.configuration.maxPeriod, this.refresh(), true);
        this._$scope.$watch(() => this.item.configuration.minPeriod, this.refresh(), true);
        this._$scope.$watch(() => this.item.configuration.rowSpacing, () => {
            this.trendHeight = sparklineHeights[this.item.configuration.rowSpacing];
        });

        this._$scope.$watch(() => this.item.configuration.aggregationColumnMode, () => {
            this.aggregationColumnMode = this.item.configuration.aggregationColumnMode
                ? aggregationModes.find(mode => mode.key === this.item.configuration.aggregationColumnMode)?.value
                : undefined;
            if (this.rowViewModels) {
                this.isLoading = true;
                try {
                    this.rowViewModels.forEach((rvm, i) => {
                        const aggregated = this._aggregationHelper.aggregate(rvm.values,
                            this.item.configuration.aggregationColumnMode);
                        if (aggregated || aggregated === 0) {
                            this.rowViewModels[i].actualAggregated = aggregated.toString();
                        }
                    });
                } catch (e) {
                    console.error(e);
                }
                this.isLoading = false;
            }
        }, true);

        if (this.isBound) {
            this._$scope.$watch(() => this.boundData, () => {
                this.updateBoundData();
            });
        } else {
            this._$scope.$on(refreshClick, this.refresh());
        }

        this._$scope.$on(isEditMode, (event, data) => {
            this.isEditMode = data;
        });

        this.isEditMode = this._$rootScope.isEditMode;
        this.hasManualInputRole = this._authorizationService.isInRoles(Roles.manualInputRoles);
        this.updateBoundData();
        this.item.exportToCsv = () => this.exportToCsv();
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    hasTarget(): boolean {
        const result = _.some(this.item.configuration.rows, r => r.isTargetVisible || r.isRangeVisible);
        return result;
    }

    hasLink(): boolean {
        const result = _.some(this.item.configuration.rows, r => r.link && r.link.hasLink);
        return result;
    }

    hasAlarm(): boolean {
        const result = _.some(this.item.configuration.rows, r => r.isAlarmVisible);
        return result;
    }

    gotoDestination(row: RowViewModel): void {
        if (row.link.dashboardGuid && row.link.dashboardGuid !== guidEmpty) {
            const selectedGuid = this._dashboardHelperService.getSelectedGroupGuid(this.selectedGroup?.guid, row);
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            const go = this._dashboardHelperService.getGotoDestination(row.link.dashboardGuid, toDictionary(this.dashboards), selectedGuid, beginDate, endDate);
            this._$state.go(go.route, go.params);
        }
    }

    getValueType(row: RowViewModel): string {
        let result = row.value;
        if (row.value === '*') {
            result = row.values && row.values.length > 0
                ? row.values[0].valueType
                : '';
        }
        return ValueTypes.getValueType(result);
    }

    clickCell(value: any, $event: angular.IAngularEvent, edit: boolean = false): void {
        $event.stopPropagation();
        this._logbookService.openModal(
            value.guid,
            value.badge,
            edit,
            this.hasManualInputRole,
            this.item.configuration.showLogbook,
            { startDate: value.date, endDate: this.findNextDate(value.date) },
            this.hasAlarm() && value.alarm > 0 ? value.alarm - 1 : undefined
        );
    }

    private setMomentFormat(): void {
        if (this.item.configuration.aggregation.mode === AggregationMode.None) {
            this.momentFormat = 'DD';
        } else {
            this.momentFormat = MomentHelper.getFormat(this.item.configuration.aggregation.period.unit);
        }
    }

    private refreshStore(store: Store): void {
        const dashboards = _.values(store.dashboards.dictionary);
        if (dashboards) {
            this.dashboardNames = this._dashboardHelperService.getDashboardNames(dashboards);
        }
        this.dashboards = dashboards;
        this.digitalSets = store.digitalSetDictionary.dictionary;
        this.viewModels = store.valueViewModels.dictionary;
        this.tags = store.tags.dictionary;
        this.identifiers = store.identifiers.dictionary;
        this.variablesTypes = store.variablesTypes.dictionary;
        this.isLoading = false;
        this.isStoreLoaded = true;
        this._whenLoaded.resolve();
    }

    private exportToCsv(): void {
        this.isLoading = true;
        try {
            const dataArray = this._tableWidgetService.getDataTable(
                this.item,
                this.aggregationColumnMode,
                this.dates,
                this.displayedRows,
                this.hasTarget(),
                this.hasAlarm()
            );
            const beginDate = this.boundData?.beginDate ? moment(this.boundData?.beginDate) : moment(this._dateRangeService.beginDate());
            this._exportToCsvService.getCsvFromData(
                dataArray,
                `Table_${this.item.name}_${beginDate.format('YYYYMMDD_HHmm')}.csv`
            );
        } catch (e) {
            this._loggerService.logError('Failed to export table to csv.');
            this._loggerService.logError(e.message);
        }
        this.isLoading = false;
    }

    private findNextDate(d: string): string {
        const date = moment(d);
        const dates = this.dates.map(d => moment(d.isoDate));
        const i = _.findIndex(dates, d => date.isSame(d));
        return (i < 0 || (i + 1) >= dates.length)
            ? this.endDate
            : dates[i + 1].toISOString();
    }

    private updateBoundData(): void {
        if (this.boundData) {
            this.beginDate = this.boundData.beginDate;
            this.endDate = this.boundData.endDate;
            this._whenLoaded.promise.then(() => this.prepareBoundData());
        }
    }

    private updateSeries(newValue: RowConfiguration[]): void {
        if (newValue) {
            this.isLoading = true;
            const rows = newValue;
            this._seriesService
                .getUpdatedGuids(rows)
                .then(data => {
                    this._rowsConfiguration = data;
                    return this._tableWidgetService.getRowViewModels(data);
                })
                .then(data => {
                    if (_.some(data, c => !c.name)) {
                        this.item.setError(new WideError(ErrorLevel.Error, missingSeries));
                    }
                    this.rowViewModels = data;
                    this.isLoading = false;
                    this.refresh()();
                })
                .catch(e => {
                    console.log(e);
                    this.isLoading = false;
                });
        }
    }

    private refresh(): () => void {
        const self = this;
        return () => {
            this.isLoading = true;
            this._whenLoaded
                .promise
                .then(() => this.doRefreshNow(self))
                .finally(() => this.isLoading = false);
        };
    }

    /**
     * Do not call directly, use refresh()
     * @see refresh()
     * @param self
     */
    private doRefreshNow(self: this): void {
        if (self._dateRangeService.initialized() && !this.isLoading && this.rowViewModels
            && this.rowViewModels.length > 0) {
            self.isLoading = true;
            this.beginDate = this._dateRangeService.beginDate() as string;
            this.endDate = this._dateRangeService.endDate() as string;
            const interval = this.item.interval();
            // if (!this.boundData) {
            if (!this.isBound) {
                self._tableWidgetService
                    .readData(this.item.configuration, interval, this._rowsConfiguration)
                    .then(response => {
                        if (response) {
                            const values = response.values;
                            const statuses = response.statuses;
                            const alarms = response.alarms;
                            const rowViewModels = this.rebuildRowViewModels();
                            this.prepareData(values, statuses, rowViewModels, alarms, response.trendValues, undefined, this.stringValues);
                        }
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                this.prepareBoundData();
            }
        }
    }

    private setStyle(dates: any[]): void {
        if (!this.item.configuration.isVertical) {
            const nameWidth = 8;
            this._colsCount = nameWidth //name
                + dates.length
                + (this.hasTarget() ? 5 : 0)
                + (this.item.configuration.isQualityVisible ? 1 : 0);
            this.nameStyle = {
                'flex-basis': nameWidth / this._colsCount * 100 + '%'
            };
            this.colStyle = {
                'flex-basis': 1 / this._colsCount * 100 + '%',
            };
        } else {
            this.nameStyle = {};
            this.colStyle = {};
        }
    }

    private prepareBoundData(): void {
        if (this.boundData) {
            const rowViewModels = this.rebuildRowViewModels();
            let allValues = this.boundData.aggregatedValues;
            allValues = allValues
                ? allValues.concat(this.boundData.aggregatedValues)
                : this.boundData.aggregatedValues;
            return this.prepareData(
                this.boundData.aggregatedValues,
                this.boundData.statuses,
                rowViewModels,
                this.boundData.alarms,
                this.boundData.values,
                this.boundData.entries,
                this.stringValues
            );
        }
    }

    // Rebuild missing row view model from queries
    private rebuildRowViewModels(): RowViewModel[] {
        const result: RowViewModel[] = [];
        const w = this.item as IWidget;
        const queries = w.executedQueries;
        const allowed = _.map(w.executedQueriesMap, m => m.valueViewModelGuid);

        if (this.rowViewModels) {
            this.rowViewModels.forEach((r: any) => {
                if (queries && r.configuration && queries[r.configuration.guid]) {
                    _.forEach(queries[r.configuration.guid], vm => {
                        if (allowed.indexOf(vm.guid) >= 0) {
                            const s = this._tableWidgetService.mapSeriesToViewmodel(vm, r.configuration);
                            result.push(s);
                        }
                    });
                } else {
                    result.push(r);
                }
            });
        }

        // remap unit using widget conversion configuration
        // has to be done after queries mapping so that we do it on query too
        // variablesTypes: Dictionary<VariableType>;
        result.forEach(s => {
            const vm = this.viewModels[s.guid];
            s.unit = this._seriesService.convertUnit(vm, this.item.configuration?.options, this.variablesTypes);
        });

        return result;
    }

    private prepareData(
        values: TagDateValue[],
        statuses: { key: string; value: number }[],
        rowViewModels: RowViewModel[],
        alarms?: { key: string; value: number }[],
        trendValues?: TagDateValue[],
        entries?: LogbookEntryDto[],
        stringValues?: TagStringValueDto[]
    ): void {
        if (this.isStoreLoaded) {
            const widgetConfiguration = this.item.configuration;
            const maxColumns = widgetConfiguration.maxColumns || 31;
            const aggregationMode = widgetConfiguration.aggregationColumnMode;

            if (widgetConfiguration.aggregation.mode === AggregationMode.None) {
                values = trendValues;
            }

            if (rowViewModels && values) {
                let dates = _.chain(values)
                    .map(d => d.timeStamp)
                    .uniq()
                    .map(v => {
                        const m = moment(v);
                        return {
                            actualDate: m.format(momentFormat),
                            displayDate: m.format(this.momentFormat),
                            isoDate: m.toISOString()
                        } as DateViewModel;
                    })
                    .value();
                const truncatedMessage = this._$translate.instant('The table was truncated to maximum number of columns : _maxColumns_', { maxColumns });
                if (dates.length > maxColumns) {
                    dates.reverse();
                    dates = dates.slice(0, maxColumns);
                    dates.reverse();
                    this.item.setError(new WideError(ErrorLevel.Info, truncatedMessage));
                } else {
                    const err = this.item.getError();
                    if (err && err.message === truncatedMessage) {
                        this.item.setError(null);
                    }
                }
                this.dates = dates;
                this.setStyle(this.dates);
                rowViewModels.forEach(rowViewModel => {
                    const customFields = this.tags[rowViewModel.guid]
                        ? this.tags[rowViewModel.guid].customProperties
                        : this.identifiers[rowViewModel.guid]
                            ? this.identifiers[rowViewModel.guid].customProperties
                            : null;
                    rowViewModel.customProperties = customFields;

                    let curVal = values
                        .filter(r =>
                            r.guid === rowViewModel.guid &&
                            (r.valueType === rowViewModel.value || rowViewModel.value === '*')
                        );
                    const curValIsoDates = curVal.map(v => new Date(Date.parse(v.timeStamp as string)).toISOString());
                    const missing = dates
                        .filter(d => _.every(curValIsoDates, v => v !== d.isoDate));
                    //If no data, create empty rows
                    curVal = curVal
                        .concat(missing.map(d => {
                            const res: TagDateValue = {
                                guid: rowViewModel.guid,
                                valueType: rowViewModel.value,
                                comment: 'missing',
                                secondaryValue: -1,
                                timeStamp: d.isoDate,
                                value: null
                            };
                            return res;
                        }))
                        .sort((a, b) => a.timeStamp > b.timeStamp ? 1 : -1);
                    if (curVal.length > maxColumns) {
                        curVal.reverse();
                        curVal = curVal.slice(0, maxColumns);
                        curVal.reverse();
                    }
                    const logbookEntries = this._logbookService.getEntriesRegarding(entries, rowViewModel.guid);
                    const format = this._formatNumberHelper.getFormatter(rowViewModel.decimals);
                    const currentValues = curVal.map(e => this.mapCurrentValue(e, rowViewModel, format, logbookEntries, stringValues, rowViewModel.decimals));
                    if (aggregationMode !== AggregationMode.None) {
                        const aggregated = this._aggregationHelper.aggregate(curVal, aggregationMode);
                        rowViewModel.actualAggregated = aggregated?.toString();
                    }
                    rowViewModel.values = currentValues;
                    const st = _.find(statuses, (s: { key: string; value: number }) => s.key === rowViewModel.guid);
                    rowViewModel.status = st ? st.value : (currentValues.filter(v => v.formattedValue === '-').length ? 2 : 0);
                    const alarm = _.find(alarms, (s: { key: string; value: number }) => s.key === rowViewModel.guid);
                    rowViewModel.alarm = rowViewModel.isAlarmVisible && alarm ? alarm.value : 0;
                    if (rowViewModel.link) {
                        rowViewModel.link.linkIcon = (rowViewModel.link.dashboardGuid === guidGroupValidationPageLink)
                            ? 'fa-check-square-o'
                            : 'fa-th-large';
                    }
                    if (this.item.configuration.hasTrend && trendValues) {
                        this._sparkLineHelper.data(trendValues as any);
                        const yDomain = this._sparkLineHelper.yDomain();
                        if (rowViewModel.originalViewModel) {
                            const min = rowViewModel.originalViewModel.min;
                            const max = rowViewModel.originalViewModel.max;
                            if (min || rowViewModel.originalViewModel.min === 0) {
                                yDomain[0] = min;
                            }
                            if (max || rowViewModel.originalViewModel.max === 0) {
                                yDomain[1] = max;
                            }
                        }
                        this._sparkLineHelper.yDomain(yDomain);
                        const xDomain: [Date, Date] = [new Date(this.beginDate), new Date(this.endDate)];
                        this._sparkLineHelper.xDomain(xDomain);
                        this._sparkLineHelper.resize(150, this.trendHeight);

                        const actualTrendValues = trendValues
                            .filter(r => r.guid === rowViewModel.guid && (r.valueType === rowViewModel.value || rowViewModel.value === '*'));
                        const ranges = rowViewModel.isAlarmVisible ? this.getRanges(actualTrendValues, this.endDate) : [];

                        const trendData: SparkLineData = {
                            domain: xDomain,
                            yDomain: yDomain,
                            path: this._sparkLineHelper.path(actualTrendValues as any),
                            ranges: ranges
                        };
                        rowViewModel.trend = trendData;
                    }
                });
            }
            this.displayedRows = rowViewModels;
        }
    }

    private mapCurrentValue(
        existing: TagDateValue,
        rowViewModel: RowViewModel,
        format: Intl.NumberFormat,
        logbookEntries: LogbookEntryDto[],
        stringValues: TagStringValueDto[],
        decimals: number
    ): FakeTagDateValue {
        const mode = this.item.configuration.aggregation.mode;
        const vm = this.viewModels[rowViewModel.guid];
        const formattedValue = this.getFormattedValue(existing, rowViewModel, vm, this.digitalSets, stringValues, mode, decimals);
        const badge = this._logbookService.getBadge(logbookEntries, existing.timeStamp);
        const value = existing
            ? this._tableWidgetService.mapValueAccordingSerie(existing, rowViewModel)
            : null;

        const res: any = {
            alarm: existing ? existing.alarm : -1,
            badge: badge,
            date: existing.timeStamp,
            formattedValue: formattedValue,
            guid: existing ? existing.guid : null,
            secondaryValue: existing ? format.format(existing.secondaryValue) : null,
            timeStamp: moment(existing.timeStamp).format(this.momentFormat),
            value: value,
            valueType: existing ? existing.valueType : null,
        };
        return res;
    }

    private getFormattedValue(existing: TagDateValue,
        rowViewModel: RowViewModel,
        vm: IValueViewModel,
        digitalSets: DigitalSetSimpleDictionary,
        stringValues: TagStringValueDto[],
        mode: AggregationMode,
        decimals: number
    ): string {
        let formattedValue = '-';
        if (existing) {
            if (rowViewModel.value === 'interpolated'
                && (rowViewModel.dataType === ActualDataType.Digital
                    || rowViewModel.dataType === ActualDataType.String)) {
                formattedValue = existing.comment;
            } else if (rowViewModel.value === ValueTypes.comment.key) {
                formattedValue = existing.comment;
            } else {
                if ((existing.value as any) === 'NaN' || existing.value === null) {
                    formattedValue = '-';
                } else {
                    formattedValue = this._valueFormattingService.formatValue(existing, vm, digitalSets, mode, decimals, null, stringValues);
                }
            }
        }
        return formattedValue;
    }

    private getRanges(values: TagDateValue[], endDate: string): AlarmRange[] {
        return values.reduce((acc: AlarmRange[], current, i, arr) => {
            let prev = acc.length > 0 ? acc[acc.length - 1] : null;

            // create intial range
            if (!prev && current.alarm > 0) {
                prev = this.getRange(current);
                acc.push(prev);
            }

            if (prev) {
                // progressively extend the range
                prev.end = current.timeStamp;

                // jump to next range
                if (prev.level !== current.alarm - 1) {
                    if (current.alarm > 0) {
                        acc.push(this.getRange(current));
                    } else {
                        acc.push(null);
                    }
                    // special case for end
                } else if (i === arr.length - 1) {
                    prev.end = endDate;
                }
            }
            return acc;
        }, []).filter(v => v !== null);
    }

    private getRange(current: TagDateValue): AlarmRange {
        return { start: current.timeStamp, end: current.timeStamp, level: (current.alarm - 1) };
    }
}

export const wtTable: ComponentDefinition = {
    componentId: 'wtTable',
    controller: TableController,
    template: require('./table.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '=?',
        isBound: '<?',
        isLoadingOutside: '<?',
        item: '=',
        rowViewModels: '<?',
        selectedGroup: '<?',
        statuses: '=?',
        stringValues: '<',
        tableStatus: '=?'
    }
};